import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageFlatList } from 'components/Page';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  eventAssessmentTemplateFieldScreenDone,
  eventAssessmentTemplateQuestionScreenDone,
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
} from 'store/actions';
import {
  selectAseSetupLibraryQuestions,
  selectRemoteCustomers,
  selectRemoteFieldFields,
  selectRemoteFieldGroupCheckTypes,
  selectRemoteFieldGroups,
  selectRemoteFieldSectionIdsByOrganisation,
  selectRemoteFieldSections,
  selectRemoteOrganisation,
} from 'store/remote/selectors';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { Icon, ICONS } from 'components/Icon';
import { Platform, View } from 'react-native';
import { ListRenderItem } from '@shopify/flash-list';
import { useEsc } from 'hooks/useEsc';
import { useConfirm, useConfirmUnsaved } from 'hooks/useConfirm';
import { useSelector } from 'hooks/useSelector';
import { CardRow } from 'components/CardRow';
import { Card } from 'components/Card';
import { theme } from 'lib/theme';
import { AssessmentTemplate, FieldSettingsTypes } from 'store/remote/types';
import { produce } from 'immer';
import { Button, ButtonTheme } from 'components/Button';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { Menu } from 'components/Menu';
import { Txt } from 'components/Txt';
import { pluralise } from 'lib/pluralise';
import { AssessmentQuestionType, FieldTypeKey } from 'types/types';
import { cloneDeep, isEqual, sortBy } from 'lodash';
import { CardGeneric } from 'components/CardGeneric';
import { smartText, str } from 'lib/str';
import { doAddEvent, doRemoveEvents } from 'lib/events';
import { randomKey } from 'lib/riskbase';

type Item = {
  listKey: string;
  firstGroup?: boolean;
  first?: boolean;
  last?: boolean;
  lastInSection?: boolean;
} & (
  | {
      type: 'section';
      sectionId: number;
    }
  | {
      type: 'group';
      groupId: number;
    }
  | {
      type: 'fields';
      label: string;
      label2: string;
      groupId: number;
    }
  | {
      type: 'field';
      groupId: number;
      fieldId: number;
    }
  | {
      type: 'check';
      groupId: number;
      checkId: string;
    }
  | {
      type: 'questions';
      groupId: number;
    }
  | {
      type: 'blank' | 'intro' | 'splitter' | 'other';
    }
);

const DefaultFieldSettingsText =
  'This is the default setting for all fields in this group. ' +
  'Customise per field below';

const DefaultFieldSettings: AssessmentTemplate['fieldsConfig'][string]['groups'][string]['fields'][string] =
  {
    editable: null,
    include: null,
    required: null,
    includeReport: null,
  };

export const FieldsScreen: React.FC<
  ModalScreenProps<ModalScreens.ASESETUPTEMPLATEFIELDS>
> = ({ route, navigation }) => {
  const dispatch = useDispatch();
  const confirm = useConfirm();
  const confirmUnsaved = useConfirmUnsaved();
  const { organisationId, eventKey, fieldsConfig } = route.params;

  const incomingQuestionsKey = React.useRef(randomKey());

  const organisationHasSectionIds = useSelector(
    selectRemoteFieldSectionIdsByOrganisation,
  );

  const mainOrganisation = useSelector(selectRemoteOrganisation)!;
  const customers = useSelector(selectRemoteCustomers)!;
  const organisation = React.useMemo(() => {
    if (mainOrganisation.id === organisationId) {
      return mainOrganisation;
    }
    return customers[organisationId];
  }, [customers, mainOrganisation, organisationId]);
  const isMainOrganisation = mainOrganisation.id === organisation?.id;

  const questions = useSelector(selectAseSetupLibraryQuestions)!;
  const sections = useSelector(selectRemoteFieldSections)!;
  const groups = useSelector(selectRemoteFieldGroups)!;
  const fields = useSelector(selectRemoteFieldFields)!;
  const groupCheckTypes = useSelector(selectRemoteFieldGroupCheckTypes)!;

  const isDirty = React.useRef(false);
  const groupIdRef = React.useRef<number>(0);

  const initFieldsConfig = {
    action: fieldsConfig[organisationId]?.action ?? null,
    groups: fieldsConfig[organisationId]?.groups ?? {},
  };

  const [data, setData] = React.useState<typeof initFieldsConfig>(
    cloneDeep(initFieldsConfig),
  );

  const checkIsDirty = () => !isEqual(initFieldsConfig, data);
  isDirty.current = checkIsDirty();

  const handleSave = () => {
    dispatch(NAVIGATE_MODAL_BACK());
    if (!fieldsConfig[organisationId] || isDirty.current) {
      dispatch(
        eventAssessmentTemplateFieldScreenDone({
          key: eventKey,
          value: { ...fieldsConfig, [organisationId]: data },
        }),
      );
    }
  };

  const handleCancel = () => {
    if (isDirty.current) {
      confirmUnsaved({
        onSave: handleSave,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  };

  const isDirtyVar = isDirty.current;
  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyVar });
  }, [navigation, isDirtyVar]);
  useEsc(handleCancel);

  const [showAllFieldsForGroups, setShowAllFieldsForGroups] = React.useState<
    number[]
  >([]);

  const items = React.useMemo(
    () =>
      (organisationHasSectionIds?.[organisationId] ?? []).reduce<Item[]>(
        (acc, sectionId) => {
          const sectionItems: typeof acc = [];
          const section = sections[sectionId];
          if (section) {
            section.groupIds.forEach((groupId) => {
              const group = groups[groupId];
              if (group) {
                const groupItems: typeof acc = [];
                const dataGroup = data.groups[groupId];
                if (dataGroup) {
                  const showAllFields =
                    showAllFieldsForGroups.includes(groupId);
                  if (group.fieldIds.length) {
                    let countHiddenFields = 0;
                    let countCustomisedFields = 0;
                    groupItems.push({
                      type: 'splitter',
                      listKey: `splitter-${groupId}-fields`,
                    });
                    group.fieldIds.forEach((fieldId) => {
                      const field = fields[fieldId]!;
                      const dataField =
                        dataGroup.fields[fieldId] || DefaultFieldSettings;
                      if (
                        showAllFields ||
                        dataField.editable !== null ||
                        dataField.include !== null ||
                        dataField.required !== null ||
                        dataField.includeReport !== null
                      ) {
                        groupItems.push({
                          type: 'field',
                          listKey: `field-${field.id}`,
                          groupId: group.id,
                          fieldId: field.id,
                        });
                        countCustomisedFields += 1;
                      } else {
                        countHiddenFields += 1;
                      }
                    });
                    if (countHiddenFields) {
                      groupItems.push({
                        type: 'fields',
                        listKey: `fields-${group.id}`,
                        label: countCustomisedFields
                          ? pluralise(
                              countHiddenFields,
                              `1 other field uses the group${str.apos}s default settings`,
                              `{x} other fields use the group${str.apos}s default settings`,
                            )
                          : pluralise(
                              countHiddenFields,
                              `1 field uses the group${str.apos}s default settings`,
                              `{x} fields use the group${str.apos}s default settings`,
                            ),
                        label2: 'Press to customise...',
                        groupId: group.id,
                      });
                    }
                  }

                  if (Object.values(group.checks).length) {
                    groupItems.push({
                      type: 'splitter',
                      listKey: `splitter-${groupId}-checks`,
                    });
                    sortBy(Object.values(group.checks), 'name').forEach(
                      (check) => {
                        groupItems.push({
                          type: 'check',
                          listKey: `check-${check.id}`,
                          groupId: group.id,
                          checkId: check.id,
                        });
                      },
                    );
                  }

                  if (group.repeatable) {
                    groupItems.push({
                      type: 'splitter',
                      listKey: `splitter-${groupId}-questions`,
                    });
                    groupItems.push({
                      type: 'questions',
                      listKey: `questions-${group.id}`,
                      groupId: group.id,
                    });
                  }
                  if (groupItems.length) {
                    groupItems[groupItems.length - 1]!.last = true;
                  }
                }
                sectionItems.push(
                  {
                    type: 'group',
                    listKey: `group-${group.id}`,
                    groupId: group.id,
                    firstGroup: !sectionItems.length,
                    first: true,
                    last: !groupItems.length,
                  },
                  ...groupItems,
                );
              }
            });
            if (sectionItems.length) {
              const lastSectionItem = sectionItems[sectionItems.length - 1];
              if (lastSectionItem) {
                lastSectionItem.last = true;
                lastSectionItem.lastInSection = true;
              }
              acc.push(
                {
                  type: 'section',
                  listKey: `section-${section.id}`,
                  sectionId: section.id,
                },
                ...sectionItems,
              );
            }
          }
          return acc;
        },
        [
          {
            type: 'blank',
            listKey: 'blank',
          },
          {
            type: 'intro',
            listKey: 'intro',
          },
        ],
      ),
    [
      organisationHasSectionIds,
      organisationId,
      sections,
      groups,
      data.groups,
      showAllFieldsForGroups,
      fields,
    ],
  );

  const handlePressGroup = React.useCallback(
    (groupId: number, withoutConfirm?: boolean) => {
      setData(
        produce((draft) => {
          const group = groups[groupId];
          if (group) {
            const dataGroups = draft.groups[groupId];
            if (dataGroups) {
              const fieldsCustomised = group.fieldIds.some((fieldId) => {
                const field = dataGroups.fields[fieldId];
                return (
                  field && (field.editable !== null || field.include !== null)
                );
              });
              if (!withoutConfirm && fieldsCustomised) {
                confirm({
                  text: 'Removing this group will also remove field customisations',
                  onConfirm: () => handlePressGroup(groupId, true),
                  destructive: true,
                  confirmText: 'Continue',
                });
                return;
              }
              delete draft.groups[groupId];
            } else {
              draft.groups[groupId] = {
                questionIds: [],
                fieldsEditable: true,
                fieldsInclude: true,
                fieldsRequired: false,
                fieldsIncludeReport: true,
                checks: {},
                fields: Object.fromEntries(
                  group.fieldIds.map((fieldId) => [
                    fieldId,
                    { ...DefaultFieldSettings },
                  ]),
                ),
              };
            }
          }
        }),
      );
    },
    [confirm, groups],
  );

  const handlePressGroupFieldsVisible = React.useCallback(
    (groupId: number, setting: boolean) => {
      setData(
        produce((draft) => {
          const draftGroup = draft.groups[groupId];
          if (draftGroup) {
            draftGroup.fieldsInclude = setting;
          }
        }),
      );
    },
    [],
  );

  const handlePressGroupFieldsEditable = React.useCallback(
    (groupId: number, setting: boolean) => {
      setData(
        produce((draft) => {
          const draftGroup = draft.groups[groupId];
          if (draftGroup) {
            draftGroup.fieldsEditable = setting;
          }
        }),
      );
    },
    [],
  );

  const handlePressGroupFieldsRequired = React.useCallback(
    (groupId: number, setting: boolean) => {
      setData(
        produce((draft) => {
          const draftGroup = draft.groups[groupId];
          if (draftGroup) {
            draftGroup.fieldsRequired = setting;
          }
        }),
      );
    },
    [],
  );

  const handlePressGroupFieldsIncludeReport = React.useCallback(
    (groupId: number, setting: boolean) => {
      setData(
        produce((draft) => {
          const draftGroup = draft.groups[groupId];
          if (draftGroup) {
            draftGroup.fieldsIncludeReport = setting;
          }
        }),
      );
    },
    [],
  );

  const handlePressGroupFieldVisible = React.useCallback(
    (groupId: number, fieldId: number) => {
      setData(
        produce((draft) => {
          const draftGroup = draft.groups[groupId];
          if (draftGroup) {
            const draftField = draftGroup.fields[fieldId];
            if (draftField) {
              draftField.include =
                draftField.include === null
                  ? true
                  : draftField.include === true
                    ? false
                    : null;
            } else {
              draftGroup.fields[fieldId] = {
                ...DefaultFieldSettings,
                include: true,
              };
            }
          }
        }),
      );
    },
    [],
  );

  const handlePressGroupCheck = React.useCallback(
    (groupId: number, checkId: string) => {
      setData(
        produce((draft) => {
          const draftGroup = draft.groups[groupId];
          if (draftGroup) {
            draftGroup.checks[checkId] = {
              include: !draftGroup.checks[checkId]?.include,
            };
          }
        }),
      );
    },
    [],
  );

  const handlePressGroupFieldEditable = React.useCallback(
    (groupId: number, fieldId: number) => {
      setData(
        produce((draft) => {
          const draftGroup = draft.groups[groupId];
          if (draftGroup) {
            const draftField = draftGroup.fields[fieldId];
            if (draftField) {
              draftField.editable =
                draftField.editable === null
                  ? true
                  : draftField.editable === true
                    ? false
                    : null;
            } else {
              draftGroup.fields[fieldId] = {
                ...DefaultFieldSettings,
                editable: true,
              };
            }
          }
        }),
      );
    },
    [],
  );

  const handlePressGroupFieldRequired = React.useCallback(
    (groupId: number, fieldId: number) => {
      setData(
        produce((draft) => {
          const draftGroup = draft.groups[groupId];
          if (draftGroup) {
            const draftField = draftGroup.fields[fieldId];
            if (draftField) {
              draftField.required =
                draftField.required === null
                  ? true
                  : draftField.required === true
                    ? false
                    : null;
            } else {
              draftGroup.fields[fieldId] = {
                ...DefaultFieldSettings,
                required: true,
              };
            }
          }
        }),
      );
    },
    [],
  );

  const handlePressGroupFieldIncludeReport = React.useCallback(
    (groupId: number, fieldId: number) => {
      setData(
        produce((draft) => {
          const draftGroup = draft.groups[groupId];
          if (draftGroup) {
            const draftField = draftGroup.fields[fieldId];
            if (draftField) {
              draftField.includeReport =
                draftField.includeReport === null
                  ? true
                  : draftField.includeReport === true
                    ? false
                    : null;
            } else {
              draftGroup.fields[fieldId] = {
                ...DefaultFieldSettings,
                includeReport: true,
              };
            }
          }
        }),
      );
    },
    [],
  );

  const handlePressSetGroupQuestions = React.useCallback(
    (groupId: number) => {
      const dataGroup = data.groups[groupId];
      groupIdRef.current = groupId;
      dispatch(
        NAVIGATE_MODAL({
          screen: ModalScreens.ASESETUPTEMPLATEQUESTIONLIST,
          params: {
            questionIds: dataGroup?.questionIds || [],
            eventKey: incomingQuestionsKey.current,
          },
        }),
      );
    },
    [data.groups, dispatch],
  );

  const handleDelete = React.useCallback(() => {
    confirm({
      title: 'Remove Customisation',
      text: 'Continuing may delete data in assessments',
      confirmText: 'Remove',
      destructive: true,
      onConfirm: () => {
        dispatch(NAVIGATE_MODAL_BACK());
        if (fieldsConfig[organisationId]) {
          dispatch(
            eventAssessmentTemplateFieldScreenDone({
              key: eventKey,
              value: {
                ...fieldsConfig,
                [organisationId]: { action: 'delete', groups: {} },
              },
            }),
          );
        }
      },
    });
  }, [confirm, dispatch, fieldsConfig, eventKey, organisationId]);

  React.useEffect(() => {
    const events = [
      doAddEvent(
        eventAssessmentTemplateQuestionScreenDone,
        ({ payload: { key, value } }) => {
          if (key === incomingQuestionsKey.current) {
            setData(
              produce((draft) => {
                const group = draft.groups[groupIdRef.current];
                if (group) {
                  group.questionIds = value;
                }
              }),
            );
          }
        },
      ),
    ];
    return () => {
      doRemoveEvents(events);
    };
  }, [dispatch]);

  const renderItem: ListRenderItem<Item> = React.useCallback(
    ({ item }) => {
      if (item.type === 'blank') {
        return <View />;
      }
      if (item.type === 'splitter') {
        return (
          <SeparatorHorizontal marginHorizontal={theme.paddingHorizontal} />
        );
      }
      if (item.type === 'intro') {
        return isMainOrganisation ? (
          <View>
            <Card
              explanation={`Choose which fields appear in assessments and which are editable by assessors.${
                Object.keys(customers).length
                  ? ' The same fields will be used for your customers if they have the fields available to them.\n\n' +
                    'A customisation can be added for a customer on the previous page.'
                  : ''
              }`}
            />
            <SeparatorHorizontal withMarginBottom />
          </View>
        ) : (
          <>
            <Card
              explanation={
                'This field customisation will be used when performing ' +
                `assessments for ${organisation?.name}. Choose which fields appear in assessments and which are editable by assessors.`
              }
            />
            <SeparatorHorizontal withMarginBottom />
            <CardGeneric style={{ marginBottom: theme.paddingVertical }}>
              <Button
                text="Remove Customisation"
                destructive
                onPress={handleDelete}
                buttonTheme={ButtonTheme.themeless}
                style={{ alignSelf: 'center' }}
              />
            </CardGeneric>
            <SeparatorHorizontal withMarginBottom />
          </>
        );
      }
      if (item.type === 'section') {
        const section = sections[item.sectionId]!;
        return (
          <Card
            label={section.name}
            margin={false}
            style={{
              backgroundColor:
                Platform.OS === 'web'
                  ? theme.colors.background
                  : theme.colors.background80,
            }}
            labelStyle={{ marginTop: theme.paddingHorizontalHalf }}
          />
        );
      }
      if (item.type === 'group') {
        const dataGroup = data.groups[item.groupId];
        const group = groups[item.groupId]!;
        return (
          <Card
            separator={null}
            style={{
              marginTop: !item.firstGroup ? theme.paddingHorizontalHalf : 0,
            }}
            borderRadiusTop={!!item.first}
            borderRadiusBottom={!!item.last}
            margin={!!item.lastInSection}
          >
            <CardRow
              label={group.name}
              checkedValue={!!dataGroup}
              onPress={() => handlePressGroup(item.groupId)}
              style={{ minHeight: 56 }}
            >
              {!!dataGroup && (
                <ViewWithSeparators
                  horizontal
                  separatorMargin={theme.paddingHorizontalHalf}
                >
                  <Menu
                    buttonConfig={{
                      iconColor: theme.colors.white,
                      buttonTheme: ButtonTheme.themeless,
                      icon: dataGroup.fieldsInclude
                        ? ICONS.EyeLight
                        : ICONS.EyeSlashLight,
                      style: {
                        backgroundColor: dataGroup.fieldsInclude
                          ? theme.colors.green
                          : theme.colors.red,
                      },
                      styleHovered: {
                        backgroundColor: dataGroup.fieldsInclude
                          ? theme.colors.green90
                          : theme.colors.red90,
                      },
                      stylePressed: {
                        backgroundColor: dataGroup.fieldsInclude
                          ? theme.colors.green80
                          : theme.colors.red80,
                      },
                    }}
                    options={[
                      {
                        text: 'Show Field',
                        subText:
                          'Field is visible when performing an assessment',
                        icon: ICONS.EyeLight,
                        iconColor: theme.colors.green,
                        onPress: () =>
                          handlePressGroupFieldsVisible(item.groupId, true),
                      },
                      {
                        text: 'Hide Field',
                        subText:
                          'Field is not visible when performing an assessment',
                        icon: ICONS.EyeSlashLight,
                        iconColor: theme.colors.red,
                        onPress: () =>
                          handlePressGroupFieldsVisible(item.groupId, false),
                      },
                      DefaultFieldSettingsText,
                    ]}
                  />
                  <Menu
                    buttonConfig={{
                      iconColor: theme.colors.white,
                      buttonTheme: ButtonTheme.themeless,
                      icon: dataGroup.fieldsEditable
                        ? ICONS.PencilLight
                        : ICONS.PencilSlashLight,
                      style: {
                        backgroundColor: dataGroup.fieldsEditable
                          ? theme.colors.green
                          : theme.colors.red,
                      },
                      styleHovered: {
                        backgroundColor: dataGroup.fieldsEditable
                          ? theme.colors.green90
                          : theme.colors.red90,
                      },
                      stylePressed: {
                        backgroundColor: dataGroup.fieldsEditable
                          ? theme.colors.green80
                          : theme.colors.red80,
                      },
                    }}
                    options={[
                      {
                        text: 'Editable Field',
                        subText:
                          'Field can be edited when performing the assessment',
                        icon: ICONS.PencilLight,
                        iconColor: theme.colors.green,
                        onPress: () =>
                          handlePressGroupFieldsEditable(item.groupId, true),
                      },
                      {
                        text: 'Read-only Field',
                        subText:
                          'Field is read-only when performing the assessment',
                        icon: ICONS.PencilSlashLight,
                        iconColor: theme.colors.red,
                        onPress: () =>
                          handlePressGroupFieldsEditable(item.groupId, false),
                      },
                      DefaultFieldSettingsText,
                    ]}
                  />
                  <Menu
                    buttonConfig={{
                      iconColor: theme.colors.white,
                      buttonTheme: ButtonTheme.themeless,
                      icon: dataGroup.fieldsRequired
                        ? ICONS.ReqLight
                        : ICONS.ReqSlashLight,
                      style: {
                        backgroundColor: dataGroup.fieldsRequired
                          ? theme.colors.green
                          : theme.colors.red,
                      },
                      styleHovered: {
                        backgroundColor: dataGroup.fieldsRequired
                          ? theme.colors.green90
                          : theme.colors.red90,
                      },
                      stylePressed: {
                        backgroundColor: dataGroup.fieldsRequired
                          ? theme.colors.green80
                          : theme.colors.red80,
                      },
                      accessibilityLabel: smartText(
                        dataGroup.fieldsRequired
                          ? 'Fields are required by default'
                          : 'Fields are not required by default',
                      ),
                      accessibilityHint:
                        'Open menu to choose if field is required',
                    }}
                    options={[
                      {
                        text: 'Field Required',
                        subText: 'Field is required to submit assessment',
                        icon: ICONS.ReqLight,
                        iconColor: theme.colors.green,
                        onPress: () =>
                          handlePressGroupFieldsRequired(item.groupId, true),
                      },
                      {
                        text: 'Field Not Required',
                        subText: 'Field is not required to submit assessment',
                        icon: ICONS.ReqSlashLight,
                        iconColor: theme.colors.red,
                        onPress: () =>
                          handlePressGroupFieldsRequired(item.groupId, false),
                      },
                      DefaultFieldSettingsText,
                    ]}
                  />
                  <Menu
                    buttonConfig={{
                      iconColor: theme.colors.white,
                      buttonTheme: ButtonTheme.themeless,
                      icon: dataGroup.fieldsIncludeReport
                        ? ICONS.BallotCheckLight
                        : ICONS.BallotCheckSlashLight,
                      style: {
                        backgroundColor: dataGroup.fieldsIncludeReport
                          ? theme.colors.green
                          : theme.colors.red,
                      },
                      styleHovered: {
                        backgroundColor: dataGroup.fieldsIncludeReport
                          ? theme.colors.green90
                          : theme.colors.red90,
                      },
                      stylePressed: {
                        backgroundColor: dataGroup.fieldsIncludeReport
                          ? theme.colors.green80
                          : theme.colors.red80,
                      },
                      accessibilityLabel: smartText(
                        dataGroup.fieldsIncludeReport
                          ? 'Fields are shown in customer reports by default'
                          : 'Fields are not shown in customer reports by default',
                      ),
                      accessibilityHint:
                        'Open menu to choose if field is shown in customer reports',
                    }}
                    options={[
                      {
                        text: 'Show Field in Report',
                        subText: 'Field is shown in customer reports',
                        icon: ICONS.BallotCheckLight,
                        iconColor: theme.colors.green,
                        onPress: () =>
                          handlePressGroupFieldsIncludeReport(
                            item.groupId,
                            true,
                          ),
                      },
                      {
                        text: 'Hide Field In Report',
                        subText: 'Field is not shown in customer reports',
                        icon: ICONS.BallotCheckSlashLight,
                        iconColor: theme.colors.red,
                        onPress: () =>
                          handlePressGroupFieldsIncludeReport(
                            item.groupId,
                            false,
                          ),
                      },
                      DefaultFieldSettingsText,
                    ]}
                  />
                </ViewWithSeparators>
              )}
            </CardRow>
            {!item.last && <SeparatorHorizontal />}
          </Card>
        );
      }
      if (item.type === 'check') {
        const dataGroup = data.groups[item.groupId];
        const group = groups[item.groupId]!;
        const check = group.checks[item.checkId]!;
        const checkType = groupCheckTypes[check.typeId]!;
        return (
          <>
            <Card
              separator={null}
              borderRadiusTop={!!item.first}
              borderRadiusBottom={!!item.last}
              margin={!!item.lastInSection}
            >
              <CardRow
                label={
                  check.name +
                  (check.name !== checkType.name ? ` (${checkType.name})` : '')
                }
                onSwitchChange={() =>
                  handlePressGroupCheck(item.groupId, item.checkId)
                }
                switchValue={!!dataGroup?.checks[item.checkId]?.include}
              />
            </Card>
            {!item.last && <SeparatorHorizontal />}
          </>
        );
      }
      if (item.type === 'questions') {
        const dataGroup = data.groups[item.groupId];
        const { checklistIds, questionIds } = (
          dataGroup?.questionIds ?? []
        ).reduce<{
          checklistIds: number[];
          questionIds: number[];
        }>(
          (accQC, questionId) => {
            accQC[
              questions[questionId]?.type === AssessmentQuestionType.checklist
                ? 'checklistIds'
                : 'questionIds'
            ].push(questionId);
            return accQC;
          },
          { checklistIds: [], questionIds: [] },
        );
        return (
          <Card
            separator={null}
            borderRadiusTop={!!item.first}
            borderRadiusBottom={!!item.last}
            margin={!!item.lastInSection}
          >
            <CardRow
              onPress={() => handlePressSetGroupQuestions(item.groupId)}
              childrenStyle={{ flexDirection: 'row' }}
            >
              <Icon
                source={ICONS.CircleCSolid}
                color={theme.colors.checklist}
                style={{
                  marginRight: theme.paddingHorizontal / 2,
                }}
              />
              <Txt>
                {!!checklistIds.length && (
                  <Txt weight="bold">{checklistIds.length} </Txt>
                )}
                {pluralise(
                  checklistIds.length,
                  'Checklist',
                  'Checklists',
                  'No Checklists',
                )}
              </Txt>
              <Icon
                source={ICONS.CircleQSolid}
                color={theme.colors.question}
                style={{
                  marginRight: theme.paddingHorizontal / 2,
                  marginLeft: theme.paddingHorizontal,
                }}
              />
              <Txt>
                {!!questionIds.length && (
                  <Txt weight="bold">{questionIds.length} </Txt>
                )}
                {pluralise(
                  questionIds.length,
                  'Question',
                  'Questions',
                  'No Questions',
                )}
              </Txt>
            </CardRow>
            {!item.last && <SeparatorHorizontal />}
          </Card>
        );
      }
      if (item.type === 'fields') {
        return (
          <Card
            separator={null}
            borderRadiusTop={!!item.first}
            borderRadiusBottom={!!item.last}
            margin={!!item.lastInSection}
          >
            <CardRow
              style={{ minHeight: 0 }}
              label={item.label}
              label2={item.label2}
              onPress={() =>
                setShowAllFieldsForGroups((ex) => [...ex, item.groupId])
              }
              label2Color={theme.colors.blue}
            />
            {!item.last && <SeparatorHorizontal />}
          </Card>
        );
      }
      if (item.type === 'field') {
        const dataGroup = data.groups[item.groupId];
        const field = fields[item.fieldId]!;
        const fieldType = FieldSettingsTypes[field.type];
        const dataField = dataGroup?.fields[item.fieldId] || {
          editable: null,
          include: null,
          required: null,
          includeReport: null,
        };
        const fieldIsVisible =
          dataField.include !== null
            ? dataField.include
            : dataGroup?.fieldsInclude;
        const fieldIsEditable =
          fieldIsVisible && dataField.editable !== null
            ? dataField.editable
            : dataGroup?.fieldsEditable;
        const fieldCanBeRequired =
          fieldType.id !== FieldTypeKey.group &&
          fieldType.id !== FieldTypeKey.grouplight;
        return (
          <Card
            separator={null}
            borderRadiusTop={!!item.first}
            borderRadiusBottom={!!item.last}
            margin={!!item.lastInSection}
          >
            <CardRow
              style={{ minHeight: 0 }}
              contentStyle={{
                paddingTop: 3,
                paddingBottom: 3,
                minHeight: undefined,
              }}
              label={field.name}
              icon={
                field.multiSelect
                  ? fieldType.iconMultiSelect || fieldType.icon
                  : fieldType.icon
              }
            >
              <ViewWithSeparators
                horizontal
                separatorMargin={theme.paddingHorizontalHalf}
              >
                <Button
                  buttonTheme={ButtonTheme.themeless}
                  icon={
                    dataField.include !== null
                      ? dataField.include
                        ? ICONS.EyeLight
                        : ICONS.EyeSlashLight
                      : dataGroup?.fieldsInclude
                        ? ICONS.EyeLight
                        : ICONS.EyeSlashLight
                  }
                  iconColor={
                    dataField.include !== null
                      ? theme.colors.white
                      : dataGroup?.fieldsInclude
                        ? theme.colors.green
                        : theme.colors.red
                  }
                  style={
                    dataField.include !== null
                      ? {
                          backgroundColor: dataField.include
                            ? theme.colors.green
                            : theme.colors.red,
                        }
                      : undefined
                  }
                  styleHovered={
                    dataField.include !== null
                      ? {
                          backgroundColor: dataField.include
                            ? theme.colors.green90
                            : theme.colors.red90,
                        }
                      : undefined
                  }
                  stylePressed={
                    dataField.include !== null
                      ? {
                          backgroundColor: dataField.include
                            ? theme.colors.green80
                            : theme.colors.red80,
                        }
                      : undefined
                  }
                  onPress={() =>
                    handlePressGroupFieldVisible(item.groupId, item.fieldId)
                  }
                />
                <Button
                  buttonTheme={ButtonTheme.themeless}
                  icon={
                    !fieldIsVisible
                      ? ICONS.DashLight
                      : dataField.editable !== null
                        ? dataField.editable
                          ? ICONS.PencilLight
                          : ICONS.PencilSlashLight
                        : dataGroup?.fieldsEditable
                          ? ICONS.PencilLight
                          : ICONS.PencilSlashLight
                  }
                  iconColor={
                    !fieldIsVisible
                      ? theme.colors.fieldValueText
                      : dataField.editable !== null
                        ? theme.colors.white
                        : dataGroup?.fieldsEditable
                          ? theme.colors.green
                          : theme.colors.red
                  }
                  style={
                    fieldIsVisible && dataField.editable !== null
                      ? {
                          backgroundColor: dataField.editable
                            ? theme.colors.green
                            : theme.colors.red,
                        }
                      : undefined
                  }
                  styleHovered={
                    dataField.editable !== null
                      ? {
                          backgroundColor: dataField.editable
                            ? theme.colors.green90
                            : theme.colors.red90,
                        }
                      : undefined
                  }
                  stylePressed={
                    dataField.editable !== null
                      ? {
                          backgroundColor: dataField.editable
                            ? theme.colors.green80
                            : theme.colors.red80,
                        }
                      : undefined
                  }
                  onPress={
                    fieldIsVisible
                      ? () =>
                          handlePressGroupFieldEditable(
                            item.groupId,
                            item.fieldId,
                          )
                      : null
                  }
                />
                <Button
                  accessibilityLabel={
                    !fieldCanBeRequired
                      ? 'This field type cannot be required'
                      : !fieldIsVisible || !fieldIsEditable
                        ? 'Field is not visible and cannot be required'
                        : dataField.required !== null
                          ? dataField.required
                            ? 'Field value is required'
                            : 'Field value is not required'
                          : dataGroup?.fieldsRequired
                            ? 'Field value is required'
                            : 'Field value is not required'
                  }
                  accessibilityHint="Open menu to choose if field is required"
                  buttonTheme={ButtonTheme.themeless}
                  icon={
                    !fieldIsVisible || !fieldIsEditable || !fieldCanBeRequired
                      ? ICONS.DashLight
                      : dataField.required !== null
                        ? dataField.required
                          ? ICONS.ReqLight
                          : ICONS.ReqSlashLight
                        : dataGroup?.fieldsRequired
                          ? ICONS.ReqLight
                          : ICONS.ReqSlashLight
                  }
                  iconColor={
                    !fieldIsVisible || !fieldIsEditable || !fieldCanBeRequired
                      ? theme.colors.fieldValueText
                      : dataField.required !== null
                        ? theme.colors.white
                        : dataGroup?.fieldsRequired
                          ? theme.colors.green
                          : theme.colors.red
                  }
                  style={
                    fieldIsVisible &&
                    fieldIsEditable &&
                    fieldCanBeRequired &&
                    dataField.required !== null
                      ? {
                          backgroundColor: dataField.required
                            ? theme.colors.green
                            : theme.colors.red,
                        }
                      : undefined
                  }
                  styleHovered={
                    dataField.required !== null
                      ? {
                          backgroundColor: dataField.required
                            ? theme.colors.green90
                            : theme.colors.red90,
                        }
                      : undefined
                  }
                  stylePressed={
                    dataField.required !== null
                      ? {
                          backgroundColor: dataField.required
                            ? theme.colors.green80
                            : theme.colors.red80,
                        }
                      : undefined
                  }
                  onPress={
                    fieldIsVisible && fieldIsEditable && fieldCanBeRequired
                      ? () =>
                          handlePressGroupFieldRequired(
                            item.groupId,
                            item.fieldId,
                          )
                      : null
                  }
                />
                <Button
                  accessibilityLabel={
                    !fieldIsVisible
                      ? 'Field is not visible and will not be shown in customer reports'
                      : dataField.includeReport !== null
                        ? dataField.includeReport
                          ? 'Field is shown in customer reports'
                          : 'Field is not shown in customer reports'
                        : dataGroup?.fieldsIncludeReport
                          ? 'Field is shown in customer reports'
                          : 'Field is not shown in customer reports'
                  }
                  accessibilityHint="Open menu to choose if field is shown in customer reports"
                  buttonTheme={ButtonTheme.themeless}
                  icon={
                    !fieldIsVisible
                      ? ICONS.DashLight
                      : dataField.includeReport !== null
                        ? dataField.includeReport
                          ? ICONS.BallotCheckLight
                          : ICONS.BallotCheckSlashLight
                        : dataGroup?.fieldsIncludeReport
                          ? ICONS.BallotCheckLight
                          : ICONS.BallotCheckSlashLight
                  }
                  iconColor={
                    !fieldIsVisible
                      ? theme.colors.fieldValueText
                      : dataField.includeReport !== null
                        ? theme.colors.white
                        : dataGroup?.fieldsIncludeReport
                          ? theme.colors.green
                          : theme.colors.red
                  }
                  style={
                    fieldIsVisible && dataField.includeReport !== null
                      ? {
                          backgroundColor: dataField.includeReport
                            ? theme.colors.green
                            : theme.colors.red,
                        }
                      : undefined
                  }
                  styleHovered={
                    dataField.includeReport !== null
                      ? {
                          backgroundColor: dataField.includeReport
                            ? theme.colors.green90
                            : theme.colors.red90,
                        }
                      : undefined
                  }
                  stylePressed={
                    dataField.includeReport !== null
                      ? {
                          backgroundColor: dataField.includeReport
                            ? theme.colors.green80
                            : theme.colors.red80,
                        }
                      : undefined
                  }
                  onPress={
                    fieldIsVisible
                      ? () =>
                          handlePressGroupFieldIncludeReport(
                            item.groupId,
                            item.fieldId,
                          )
                      : null
                  }
                />
              </ViewWithSeparators>
            </CardRow>
            {!item.last && <SeparatorHorizontal />}
          </Card>
        );
      }
      return null;
    },
    [
      isMainOrganisation,
      customers,
      organisation?.name,
      handleDelete,
      sections,
      data.groups,
      groups,
      handlePressGroup,
      handlePressGroupFieldsVisible,
      handlePressGroupFieldsEditable,
      handlePressGroupFieldsRequired,
      handlePressGroupFieldsIncludeReport,
      groupCheckTypes,
      handlePressGroupCheck,
      questions,
      handlePressSetGroupQuestions,
      fields,
      handlePressGroupFieldVisible,
      handlePressGroupFieldEditable,
      handlePressGroupFieldRequired,
      handlePressGroupFieldIncludeReport,
    ],
  );

  return (
    <PageFlatList
      header={
        <NavigationHeader
          heading="Fields"
          subheading={organisation?.name}
          onPressCancelButton={handleCancel}
          onPressSaveButton={handleSave}
          saveButtonText="Done"
        />
      }
      emptyConfig={{
        icon: ICONS.RBPlectrumLight,
        text: 'No Fields',
      }}
      contentPaddingTop={theme.paddingVertical - theme.paddingHorizontalHalf}
      listConfig={{
        data: items,
        keyExtractor: (item) => `${item.listKey}`,
        renderItem,
        stickyHeaderIndices: items.reduce<number[]>((acc, item, index) => {
          if (item.type === 'blank' || item.type === 'section') {
            acc.push(index);
          }
          return acc;
        }, []),
        getItemType: (item) => item.type,
        ItemSeparatorComponent: undefined,
      }}
    />
  );
};
